import {
    setError,
    setFormValues,
    setIsCreateCertificateModalOpen,
    setIsPostCreateCertificateSuccess,
    setSelectedQualification,
} from "../CreateCertificateModalSlice";
import { Popup } from "@hatch-digital-nl/abs-storybook/legacy";
import { QualificationInterface } from "../../../common/interfaces/QualificationInterface";
import { handleCreateCertificate } from "../utils/handleCreateCertificate";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { ChangeEvent, JSX, useEffect, useState } from "react";
import { useCreateCertificateMutation } from "../../../common/api/certificate";
import { useGetQualificationsQuery } from "../../../common/api/qualification";
import { useGetEmployeeMeQuery } from "../../../common/api/employee";
import { calculateExpiresAt } from "../utils/calculateExpiresAt";
import { SubmitButtonComponent } from "../components/FormComponents/SubmitButtonComponent";
import { FileInputFieldComponent } from "../components/FormComponents/FileInputFieldComponent";
import { DateInputFieldComponent } from "../components/FormComponents/DateInputFieldComponent";
import { SelectFieldComponent } from "../../../common/components/SelectFieldComponent";
import Skeleton from "react-loading-skeleton";
import { setQualificationValues } from "../utils/setQualificationValues";
import { setEmployeeValues } from "../utils/setEmployeeValues";
import { getQualificationOptions } from "../../../common/utils/selectOptions/getQualificationOptions";
import { getEducatorOptions } from "../../../common/utils/selectOptions/getEducatorOptions";
import { useGetEducatorsQuery } from "../../../common/api/educator";
import { QualificationTypeEnum } from "../../../common/enums/QualificationTypeEnum";

export const EmployeeGeneralCreateForm = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const [file, setFile] = useState<File | null>(null);
    const [
        postCreateCertificate,
        {
            isLoading: isPostCreateCertificateLoading,
            isSuccess: isPostCreateCertificateSuccess,
        },
    ] = useCreateCertificateMutation();
    const { data: employeeMeData } = useGetEmployeeMeQuery({});
    const { data: qualifications, isLoading: QualificationDataIsLoading } =
        useGetQualificationsQuery({
            items_per_page: 9999,
            filter: {
                type: QualificationTypeEnum.PERSON,
            },
        });
    const { selectedQualification, formValues, error } = useAppSelector(
        (state) => state.createCertificateModal,
    );
    const { data: educatorData, isFetching: educatorDataIsFetching } =
        useGetEducatorsQuery({
            items_per_page: 9999,
            filter: {
                qualification_id: formValues?.qualification?.selectedId,
            },
        });

    useEffect(() => {
        setQualificationValues({
            formValues,
            selectedQualification,
            dispatch,
        });
        setEmployeeValues({
            formValues,
            selectedEmployee: {
                id: employeeMeData?.id,
                user: employeeMeData?.user,
            },
            dispatch,
        });
    }, [
        selectedQualification,
        formValues,
        dispatch,
        employeeMeData,
        employeeMeData?.id,
        employeeMeData?.user,
    ]);

    useEffect(() => {
        calculateExpiresAt({
            formValues,
            dispatch,
        });
    }, [
        formValues?.validAt,
        formValues?.qualification?.validityPeriod,
        formValues?.qualification?.validityType,
        formValues?.qualification?.fixedValidUntil,
        formValues?.qualification?.selectedId,
        formValues,
        dispatch,
    ]);

    useEffect(() => {
        if (!isPostCreateCertificateSuccess) {
            dispatch(setIsPostCreateCertificateSuccess(false));
        }
    }, [isPostCreateCertificateSuccess, dispatch]);

    return (
        <Popup
            className={"c-create-certificate-modal"}
            title={"Certificaat toevoegen"}
            onCloseClick={() => {
                dispatch(setIsCreateCertificateModalOpen(false));
                dispatch(setFormValues(null));
                dispatch(setSelectedQualification(null));
                dispatch(setError(null));
            }}
        >
            {error && <div className={"c-error"}>{error}</div>}
            {QualificationDataIsLoading ? (
                <Skeleton count={4} borderRadius={"0.8rem"} height={"5.5rem"} />
            ) : (
                <form
                    className={"c-popup__content__body"}
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleCreateCertificate({
                            formValues,
                            file,
                            postCreateCertificate,
                            dispatch,
                        });
                    }}
                >
                    <SelectFieldComponent
                        id={1}
                        options={getQualificationOptions(qualifications?.data)}
                        onChange={(newValue) => {
                            const matchedQualification =
                                qualifications?.data?.find(
                                    (item: QualificationInterface) =>
                                        item.id.toString() === newValue.value,
                                );

                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    qualification: {
                                        ...formValues?.qualification,
                                        selectedId: newValue.value,
                                        selectedValue: newValue.label,
                                        validityPeriod:
                                            matchedQualification?.validity_period ??
                                            null,
                                        validityType:
                                            matchedQualification?.validity_type ??
                                            null,
                                        fixedValidUntil:
                                            matchedQualification?.fixed_valid_until ??
                                            null,
                                    },
                                }),
                            );
                        }}
                        value={{
                            value: formValues?.qualification?.selectedId,
                            label: formValues?.qualification?.selectedValue,
                        }}
                        errorMessage={"Kwalificatie is verplicht"}
                        placeHolder={"Zoeken..."}
                        title={"Selecteer kwalificatie"}
                        validityPeriod={
                            formValues?.qualification?.validityPeriod
                        }
                        validityType={formValues?.qualification?.validityType}
                        fixedValidUntil={
                            formValues?.qualification?.fixedValidUntil
                        }
                        showValidityPeriod={true}
                        showNotice={!formValues?.qualification?.selectedId}
                        noticeText={
                            'Staat jouw kwalificatie niet tussen de resultaten? Neem contact op met onze servicedesk via <a href="tel:09006611111">0900 - 66 11 11 1</a>'
                        }
                    />
                    <SelectFieldComponent
                        id={2}
                        options={getEducatorOptions(educatorData?.data)}
                        onChange={(newValue) => {
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    educator: {
                                        selectedId: newValue.value,
                                        selectedValue: newValue.label,
                                    },
                                }),
                            );
                        }}
                        value={{
                            value: formValues?.educator?.selectedId || "",
                            label: formValues?.educator?.selectedValue || "",
                        }}
                        errorMessage={"Opleider is verplicht"}
                        placeHolder={"Selecteer opleider..."}
                        title={"Selecteer opleider"}
                        isLoading={educatorDataIsFetching}
                    />
                    <DateInputFieldComponent
                        id={1}
                        value={formValues?.validAt}
                        expiresAt={formValues?.expiresAt}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            dispatch(
                                setFormValues({
                                    ...formValues,
                                    validAt: event.target.value,
                                }),
                            )
                        }
                    />
                    <FileInputFieldComponent
                        id={1}
                        formValues={formValues}
                        dispatch={dispatch}
                        setFile={setFile}
                        showNotice={formValues?.qualification}
                        noticeText={
                            "Nadat je jouw certificaat hebt toegevoegd, dient deze nog door de vestigingsmanager te worden goedgekeurd"
                        }
                    />
                    <SubmitButtonComponent
                        isPostCreateCertificateLoading={
                            isPostCreateCertificateLoading
                        }
                    />
                </form>
            )}
        </Popup>
    );
};
